import { Link, useLocation } from "react-router-dom";
import { useRef, useEffect } from "react";
import arrowDown from '../../../assets/images/arrowDown.svg';
import arrowUp from '../../../assets/images/arrowUp.svg';
import './DropDown.css';

interface DropdownItem {
  title: string;
  url?: string;
  action?: () => void;
}

interface SingleLevelDropdownMenuProps {
  buttonLabel: string;
  items: DropdownItem[];
  handleLinkClick: any;
  isOpen: boolean;
  setIsOpen: any;
  closeMenu: () => void;
}

export const DropDown = ({
  buttonLabel,
  items,
  isOpen,
  closeMenu,
  setIsOpen
}: SingleLevelDropdownMenuProps) => {
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  const handleMouseEnter = () => {
    // Очищаем таймер при входе
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    
    // Если курсор перешел на дропдаун, не закрываем
    if (dropdownRef.current?.contains(relatedTarget)) {
      return;
    }
    
    // Добавляем задержку 300ms перед закрытием
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const handleDropdownMouseEnter = () => {
    // Очищаем таймер при входе в дропдаун
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleDropdownMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    
    // Закрываем только если курсор ушел за пределы всего dropdown
    if (!dropdownRef.current?.contains(relatedTarget)) {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 10);
    }
  };

  const handleActionItemClick = (item: DropdownItem) => {
    if (item.action) {
      item.action();
    }
    setIsOpen(false);
    closeMenu();
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    closeMenu();
  };

  // Очищаем таймер при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isActiveDropdown = items.some(item => 
    item.url && location.pathname === item.url
  );

  return (
    <div 
      className='container'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className={`button ${isActiveDropdown ? 'active' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className='buttonLabel'>{buttonLabel}</span>
        <img className='chevron' src={isOpen ? arrowUp : arrowDown } alt="arrow" />
      </button>
      
      {isOpen && (
        <div 
          className='dropdown'
          ref={dropdownRef}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <ul className='menu'>
            {items.map((item, index) => {
              const isActive = item.url && location.pathname === item.url;
              
              return (
                <li key={index} className='menuItem'>
                  {item.url ? (
                    <Link
                      to={item.url}
                      className={`itemButton ${isActive ? 'active' : ''}`}
                      onClick={handleLinkClick}
                    >
                      <span className='itemTitle'>{item.title}</span>
                    </Link>
                  ) : (
                    <button
                      className='itemButton'
                      onClick={() => handleActionItemClick(item)}
                      type="button"
                    >
                      <span className='itemTitle'>{item.title}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};